/**
 * LinkedIn Sales Navigator School IDs
 * These IDs are used internally by LinkedIn to match exact schools/universities
 * Using IDs ensures exact matches rather than fuzzy text searches
 * 
 * HOW TO FIND NEW IDs:
 * 1. Go to LinkedIn Sales Navigator search
 * 2. Open browser DevTools (F12) -> Network tab
 * 3. Type a school name in the "School" field
 * 4. Look for requests to API endpoints (e.g., typeahead, search)
 * 5. Find the response with the school name and its ID
 * 6. Add to this mapping
 * 
 * IMPORTANT: Not all schools need IDs. Text search still works, just less precise.
 * Focus on adding IDs for your most commonly searched schools.
 */

export const SCHOOL_IDS: Record<string, string> = {
  // === VERIFIED IDs (from Sales Navigator) ===
  'Harvard University': '1646',  // ✅ Verified
  
  // === United States - Ivy League ===
  // 'Yale University': '',
  // 'Princeton University': '',
  // 'Columbia University': '',
  // 'University of Pennsylvania': '',
  // 'Cornell University': '',
  // 'Brown University': '',
  // 'Dartmouth College': '',
  
  // === United States - Top Tech Schools ===
  // 'Massachusetts Institute of Technology': '',
  // 'MIT': '',
  // 'Stanford University': '',
  // 'California Institute of Technology': '',
  // 'Caltech': '',
  // 'Carnegie Mellon University': '',
  // 'Georgia Institute of Technology': '',
  // 'Georgia Tech': '',
  
  // === United States - Top Business Schools ===
  // 'University of California, Berkeley': '',
  // 'UC Berkeley': '',
  // 'University of Michigan': '',
  // 'Northwestern University': '',
  // 'Duke University': '',
  // 'University of Chicago': '',
  // 'New York University': '',
  // 'NYU': '',
  
  // === United Kingdom ===
  // 'University of Oxford': '',
  // 'Oxford University': '',
  // 'University of Cambridge': '',
  // 'Cambridge University': '',
  // 'Imperial College London': '',
  // 'London School of Economics': '',
  // 'LSE': '',
  
  // === Canada ===
  // 'University of Toronto': '',
  // 'McGill University': '',
  // 'University of British Columbia': '',
  // 'UBC': '',
  
  // === Europe ===
  // 'ETH Zurich': '',
  // 'INSEAD': '',
  // 'HEC Paris': '',
  
  // === Asia ===
  // 'National University of Singapore': '',
  // 'NUS': '',
  // 'Tsinghua University': '',
  // 'Indian Institute of Technology': '',
  // 'IIT': '',
};

/**
 * Get LinkedIn school ID by school name (case-insensitive)
 * @param schoolName - The name of the school to look up
 * @returns The LinkedIn school ID if found, undefined otherwise
 */
export function getSchoolId(schoolName: string): string | undefined {
  if (!schoolName) return undefined;
  
  // Direct lookup (case-insensitive)
  const directMatch = Object.keys(SCHOOL_IDS).find(
    key => key.toLowerCase() === schoolName.toLowerCase()
  );
  
  if (directMatch) {
    return SCHOOL_IDS[directMatch];
  }
  
  // Try partial match for common abbreviations
  const normalizedInput = schoolName.toLowerCase().trim();
  
  // Check if input contains any known school name
  for (const [name, id] of Object.entries(SCHOOL_IDS)) {
    const normalizedName = name.toLowerCase();
    if (normalizedInput.includes(normalizedName) || normalizedName.includes(normalizedInput)) {
      return id;
    }
  }
  
  // Log when ID is missing (helps you discover which schools to add)
  console.log(`ℹ️  No LinkedIn ID found for school: "${schoolName}" - using text search fallback`);
  return undefined;
}
